import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, Plus, Search } from 'lucide-react';
import { api, MAX_ORGANIZATIONS, type Organization } from '../lib/api';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { SortableHeader } from '../components/ui/SortableHeader';
import { useSort } from '../lib/sort';
import { formatRelative } from '../lib/format';
import { useAuth } from '../contexts/use-auth';
import { CreateOrganizationModal } from '../components/CreateOrganizationModal';

const getOrgValue = (item: Organization, key: string): unknown => {
  switch (key) {
    case 'name':
      return item.name ?? '';
    case 'company':
      return item.company ?? '';
    case 'id':
      return item.id;
    case 'createdAt':
      return item.createdAt ?? '';
    case 'updatedAt':
      return updatedIso(item) ?? '';
    default:
      return undefined;
  }
};

/**
 * Rows written before `updatedAt` was populated still carry Amplify's
 * `_lastChangedAt` epoch, which is the same instant in a different shape.
 */
function updatedIso(org: Organization): string | null {
  if (org.updatedAt) return org.updatedAt;
  return org._lastChangedAt ? new Date(org._lastChangedAt).toISOString() : null;
}

export function Organizations() {
  const { can } = useAuth();
  const canCreate = can('x5.org.create');
  const [q, setQ] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const { data, isLoading, error } = useQuery({
    queryKey: ['organizations'],
    queryFn: () => api.listOrganizations(),
  });

  const filtered = useMemo(() => {
    const items = data?.items ?? [];
    if (!q.trim()) return items;
    const needle = q.toLowerCase();
    return items.filter(
      (o) =>
        o.name?.toLowerCase().includes(needle) ||
        o.company?.toLowerCase().includes(needle) ||
        o.id.toLowerCase().includes(needle),
    );
  }, [data, q]);

  const { sortedItems, sort, toggle } = useSort(filtered, getOrgValue);

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Organizations</h1>
          <p className="text-muted-foreground">
            {data ? `${filtered.length} of ${data.items.length} organizations` : '—'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative w-72">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by name, company, or ID…"
              className="pl-9"
            />
          </div>
          {canCreate && (
            <Button onClick={() => setCreateOpen(true)}>
              <Plus className="h-4 w-4" />
              New organization
            </Button>
          )}
        </div>
      </div>

      {data?.truncated && (
        <div className="flex gap-2 rounded-md border border-warning/40 bg-warning/10 px-4 py-3 text-sm text-warning">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <div>
            <p className="font-medium">This list is incomplete</p>
            <p className="mt-1 text-xs opacity-90">
              The Organization table holds more than the {MAX_ORGANIZATIONS}-row ceiling this portal
              reads, so some organizations are missing here and from every organization picker.
              Rows must have been added outside x5-admin.
            </p>
          </div>
        </div>
      )}

      {isLoading && <p className="text-muted-foreground">Loading…</p>}
      {error && <p className="text-destructive">{(error as Error).message}</p>}

      {data && (
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 border-b border-border">
                <tr>
                  <SortableHeader label="Name" sortKey="name" sort={sort} onToggle={toggle} />
                  <SortableHeader label="Company" sortKey="company" sort={sort} onToggle={toggle} />
                  <SortableHeader label="ID" sortKey="id" sort={sort} onToggle={toggle} />
                  <SortableHeader label="Added" sortKey="createdAt" sort={sort} onToggle={toggle} />
                  <SortableHeader
                    label="Updated"
                    sortKey="updatedAt"
                    sort={sort}
                    onToggle={toggle}
                  />
                </tr>
              </thead>
              <tbody>
                {sortedItems.map((o) => (
                  <tr
                    key={o.id}
                    className="border-b border-border last:border-0 hover:bg-muted/50 transition-colors"
                  >
                    <td className="px-4 py-3">
                      <Link
                        to={`/organizations/${o.id}`}
                        className="text-primary font-medium hover:underline"
                      >
                        {o.name ?? '(no name)'}
                      </Link>
                    </td>
                    <td className="px-4 py-3 text-muted-foreground">{o.company ?? '—'}</td>
                    <td className="px-4 py-3 font-mono text-xs text-muted-foreground">{o.id}</td>
                    <td className="px-4 py-3 text-muted-foreground" title={o.createdAt ?? ''}>
                      {formatRelative(o.createdAt)}
                    </td>
                    <td className="px-4 py-3 text-muted-foreground" title={updatedIso(o) ?? ''}>
                      {formatRelative(updatedIso(o))}
                    </td>
                  </tr>
                ))}
                {sortedItems.length === 0 && (
                  <tr>
                    <td className="px-4 py-6 text-center text-muted-foreground" colSpan={5}>
                      No matches.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {createOpen && (
        <CreateOrganizationModal
          existing={data?.items ?? []}
          onClose={() => setCreateOpen(false)}
        />
      )}
    </div>
  );
}
